import { useEffect, useState } from "react";

import LaunchVisualization, {
  LaunchVisualizationProps,
} from "@/assets/LaunchVisualization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

type LaunchChartCardProps = {
  title?: string;
  // 'auto' means legend is visible if the visualization has more than one dataset
  showLegend?: true | false | "auto";
  children?: React.ReactNode;
} & React.ComponentProps<typeof Card> &
  LaunchVisualizationProps;

export const LaunchChartCard = ({
  title = "Launch",
  datasets = [],
  showLegend = "auto",
  maxSpeed = 115,
  unit = "MPH",
  children,
  ...cardProps
}: LaunchChartCardProps) => {
  const [isLegendVisible, setLegendVisible] = useState(false);

  useEffect(() => {
    setLegendVisible(
      showLegend === true || (showLegend === "auto" && datasets.length > 1),
    );
  }, [showLegend, datasets.length]);

  return (
    <Card className="w-full max-w-lg p-1 flex flex-col" {...cardProps}>
      <CardHeader className="p-2">
        <CardTitle className="text-xs md:text-sm text-center">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-hidden flex-1">
        <Separator className="mb-2" />
        <div className="w-full h-full relative">
          <ChartContainer
            config={{}}
            className="aspect-square max-h-[350px] w-full"
          >
            <LaunchVisualization
              datasets={datasets}
              maxSpeed={maxSpeed}
              unit={unit}
            />
          </ChartContainer>
          {isLegendVisible && (
            <div className="absolute left-0 bottom-0">
              {datasets.map(({ name, color, icon }) => (
                <>
                  {icon === "circle" && (
                    <div className={`flex items-center space-x-2`} key={name}>
                      <span
                        className={cn(`inline-block w-2 h-2 rounded-full`)}
                        style={{ backgroundColor: color }}
                        aria-hidden="true"
                        data-testid={`legend-for-${name}`}
                      />
                      <h3 className="text-xs">{name}</h3>
                    </div>
                  )}
                  {icon === "triangle" && (
                    <div className={`flex items-center space-x-2`} key={name}>
                      <span
                        style={{ color: color }}
                        className={cn(`text-xs d-block`)}
                        aria-hidden="true"
                        data-testid={`legend-for-${name}`}
                      >
                        ▲
                      </span>
                      <h3 className="text-xs">{name}</h3>
                    </div>
                  )}
                </>
              ))}
            </div>
          )}
          {children}
        </div>
      </CardContent>
    </Card>
  );
};
